import React, { useEffect, useState, useCallback, useRef } from 'react';
import { personColor, useDrawerFocus } from './utils';

const TYPE_COLORS = { Incident: '#7c3aed', 'Sales Order Plan': '#0891b2', 'Project Plan': '#059669', Unknown: '#94a3b8' };
const getTC = (t) => TYPE_COLORS[t] || '#6366f1';

function Avatar({ name }) {
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const color = personColor(name);
  return (
    <div style={{ width: 36, height: 36, borderRadius: '50%', background: color + '22', color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0, border: `2px solid ${color}44` }}>
      {initials}
    </div>
  );
}

function priorityBadge(p) {
  const cls = { Critical: 'badge-critical', Highest: 'badge-critical', High: 'badge-high', Medium: 'badge-medium', Low: 'badge-low', Lowest: 'badge-low' }[p] || 'badge-default';
  return <span className={`badge ${cls}`}>{p || '—'}</span>;
}

function TicketGroup({ title, color, tickets, jiraUrl }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{title}</span>
        <span className="badge badge-blue" style={{ fontSize: 11 }}>{tickets.length}</span>
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginLeft: 'auto', color: 'var(--text-2)', transform: open ? 'rotate(90deg)' : '', transition: 'transform .2s' }}><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      {open && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginTop: 4 }}>
          {tickets.map((t, idx) => (
            <div key={t.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderBottom: idx < tickets.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background .1s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <span style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: "'IBM Plex Mono', monospace", width: 20, paddingTop: 2, flexShrink: 0 }}>{idx + 1}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                  {t.url
                    ? <a href={t.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none', background: 'var(--accent-light)', padding: '1px 7px', borderRadius: 5 }}>{t.key} ↗</a>
                    : <span className="badge badge-blue" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{t.key}</span>}
                  {priorityBadge(t.priority)}
                  <span className="badge badge-default" style={{ fontSize: 11 }}>{t.status}</span>
                  <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 100, background: getTC(t.issue_type) + '22', color: getTC(t.issue_type), fontWeight: 600 }}>{t.issue_type}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4, wordBreak: 'break-word' }}>{t.summary}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 600, color: t.age_days > 90 ? 'var(--critical)' : t.age_days > 30 ? 'var(--warn)' : 'var(--text)' }}>{t.age_days}d</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{t.time_spent || '—'}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AssigneeDrawer({ api, uploadId, assignee, onClose, jiraUrl: drawerJiraUrl }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const jiraUrl = drawerJiraUrl || localStorage.getItem('jiraUrl') || '';
  const [groupBy, setGroupBy] = useState('type');
  const [sortBy, setSortBy] = useState('age');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');

  const [error, setError] = useState(false);
  const drawerRef = useRef(null);
  useDrawerFocus(drawerRef);

  const load = useCallback((url) => {
    setLoading(true); setError(false);
    const params = url ? `?jira_url=${encodeURIComponent(url)}` : '';
    fetch(`${api}/api/uploads/${uploadId}/assignees/${encodeURIComponent(assignee)}${params}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [api, uploadId, assignee]);

  useEffect(() => { load(jiraUrl); }, [load, jiraUrl]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const filterTickets = (tickets) => {
    let result = !search ? tickets : tickets.filter(t =>
      t.key.toLowerCase().includes(search.toLowerCase()) ||
      t.summary.toLowerCase().includes(search.toLowerCase()) ||
      t.status.toLowerCase().includes(search.toLowerCase())
    );
    const dir = sortDir === 'desc' ? -1 : 1;
    const PO = ['Critical', 'Highest', 'High', 'Medium', 'Low', 'Lowest'];
    if (sortBy === 'age') result = [...result].sort((a, b) => (b.age_days - a.age_days) * dir);
    if (sortBy === 'key') result = [...result].sort((a, b) => a.key.localeCompare(b.key) * dir);
    if (sortBy === 'priority') result = [...result].sort((a, b) => (PO.indexOf(a.priority) - PO.indexOf(b.priority)) * dir);
    if (sortBy === 'status') result = [...result].sort((a, b) => a.status.localeCompare(b.status) * dir);
    return result;
  };

  return (
    <>
      <div onClick={onClose} className="drawer-overlay" />
      <div className="drawer drawer--lg" role="dialog" aria-modal="true"
        aria-label={`Tickets — ${assignee}`} tabIndex={-1} ref={drawerRef}>
        <div className="drawer-header drawer-header--gap">
          <Avatar name={assignee} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 17 }}>{assignee}</div>
            {data && <div className="drawer-subtitle">{data.ticket_count} open tickets · avg age {data.avg_age_days}d · {data.total_time_spent} logged</div>}
          </div>
          <button onClick={onClose} className="drawer-close-btn">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="drawer-toolbar">
          <input type="text" placeholder="Search tickets…" value={search} onChange={e => setSearch(e.target.value)}
            className="drawer-search" />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="drawer-select">
            <option value="age">Sort: Age</option>
            <option value="key">Sort: Key</option>
            <option value="priority">Sort: Priority</option>
            <option value="status">Sort: Status</option>
          </select>
          <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} className="drawer-sort-btn">
            {sortDir === 'desc' ? '↓' : '↑'}
          </button>
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
            {[['type', 'By Type'], ['project', 'By Project'], ['both', 'Both']].map(([v, l]) => (
              <button key={v} onClick={() => setGroupBy(v)} style={{ padding: '6px 12px', fontSize: 12, fontFamily: "'IBM Plex Sans', sans-serif", border: 'none', cursor: 'pointer', background: groupBy === v ? 'var(--accent)' : 'var(--surface)', color: groupBy === v ? '#fff' : 'var(--text-2)', fontWeight: groupBy === v ? 600 : 400 }}>{l}</button>
            ))}
          </div>
        </div>
        <div className="drawer-body" style={{ padding: '16px 24px' }}>
          {loading ? <div className="loading">Loading tickets…</div>
            : error ? <div className="empty"><p>Couldn’t load this person’s tickets — check the connection and reopen.</p></div>
            : !data ? null : (
            <>
              {(groupBy === 'type' || groupBy === 'both') && (
                <div style={{ marginBottom: groupBy === 'both' ? 32 : 0 }}>
                  {groupBy === 'both' && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>By Issue Type</div>}
                  {Object.entries(data.by_type).map(([type, tickets]) => {
                    const f = filterTickets(tickets);
                    return f.length > 0 && <TicketGroup key={type} title={type} color={getTC(type)} tickets={f} jiraUrl={jiraUrl} />;
                  })}
                </div>
              )}
              {(groupBy === 'project' || groupBy === 'both') && (
                <div>
                  {groupBy === 'both' && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>By Project</div>}
                  {Object.entries(data.by_project).map(([project, tickets]) => {
                    const f = filterTickets(tickets);
                    return f.length > 0 && <TicketGroup key={project} title={project} color="var(--accent)" tickets={f} jiraUrl={jiraUrl} />;
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function AssigneesPage({ api, uploadId, teamOnly, jiraUrl: globalJiraUrl }) {
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('tickets');
  const [sortDir, setSortDir] = useState('desc');
  const toggleDir = () => setSortDir(d => d === 'desc' ? 'asc' : 'desc');
  const [selected, setSelected] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(() => new Set(['Incident']));

  useEffect(() => {
    if (!uploadId) { setAssignees([]); return; }
    setLoading(true); setLoadError(false);
    fetch(`${api}/api/uploads/${uploadId}/assignees?team_only=${!!teamOnly}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setAssignees(d); setLoading(false); })
      .catch(() => { setLoadError(true); setLoading(false); });
  }, [uploadId, api, teamOnly]);

  // Reset to Incident-only when switching snapshot or team filter
  useEffect(() => { setSelectedTypes(new Set(['Incident'])); }, [uploadId, teamOnly]);

  if (!uploadId) return <div className="empty"><p>Select an export to view assignees.</p></div>;
  if (loading) return <div className="loading">Loading assignees…</div>;
  if (loadError) return <div className="empty"><p className="error-text">Couldn’t load assignees — check the connection and reload.</p></div>;

  // Collect all issue types present in the data; Incident always first
  const allTypes = [...new Set(assignees.flatMap(a => Object.keys(a.issue_types || {})))]
    .sort((a, b) => a === 'Incident' ? -1 : b === 'Incident' ? 1 : a.localeCompare(b));

  const getFilteredCount = (person) =>
    Object.entries(person.issue_types || {})
      .filter(([t]) => selectedTypes.size === 0 || selectedTypes.has(t))
      .reduce((sum, [, c]) => sum + c, 0);

  const toggleType = (type) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const allSelected = allTypes.length > 0 && allTypes.every(t => selectedTypes.has(t));
  const toggleAll = () => setSelectedTypes(allSelected ? new Set() : new Set(allTypes));

  let filtered = search
    ? assignees.filter(a => a.assignee.toLowerCase().includes(search.toLowerCase()))
    : assignees;
  const dir = sortDir === 'desc' ? -1 : 1;
  if (sortBy === 'tickets') filtered = [...filtered].sort((a, b) => (getFilteredCount(b) - getFilteredCount(a)) * dir);
  if (sortBy === 'time')    filtered = [...filtered].sort((a, b) => (b.time_spent_seconds - a.time_spent_seconds) * dir);
  if (sortBy === 'age')     filtered = [...filtered].sort((a, b) => (b.avg_age_days - a.avg_age_days) * dir);

  const maxFilteredCount = Math.max(...filtered.map(a => getFilteredCount(a)), 1);
  const assigned   = filtered.filter(a => a.assignee !== 'Unassigned');
  const unassigned = filtered.find(a => a.assignee === 'Unassigned');

  const activeAssigned  = assigned.filter(a => getFilteredCount(a) > 0);
  const busiest         = activeAssigned.reduce((best, a) => !best || getFilteredCount(a) > getFilteredCount(best) ? a : best, null);
  const unassignedCount = unassigned ? getFilteredCount(unassigned) : 0;

  const typeLabel = selectedTypes.size === 0
    ? 'no types selected'
    : selectedTypes.size === allTypes.length
      ? 'all types'
      : [...selectedTypes].join(', ');

  return (
    <div>
      {/* Summary cards — all reflect the active type filter */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-title">Total Assignees</div>
          <div className="card-value">{activeAssigned.length}</div>
          <div className="card-sub">With open {typeLabel}</div>
        </div>
        <div className="card">
          <div className="card-title">Busiest</div>
          <div className="card-value">{busiest?.assignee?.split(' ')[0] ?? '—'}</div>
          <div className="card-sub">{busiest ? `${getFilteredCount(busiest)} open tickets` : 'No data'}</div>
        </div>
        <div className="card">
          <div className="card-title">Unassigned</div>
          <div className="card-value" style={{ color: unassignedCount > 0 ? 'var(--warn)' : undefined }}>{unassignedCount}</div>
          <div className="card-sub">Tickets with no owner</div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="section-title" style={{ margin: 0 }}>
            All Assignees <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-2)' }}>— click a row to see their tickets</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" placeholder="Search name…" value={search} onChange={e => setSearch(e.target.value)}
              className="drawer-search" style={{ width: 'auto', flex: 'none' }} />
            <select className="upload-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="tickets">Sort: Tickets</option>
              <option value="time">Sort: Time spent</option>
              <option value="age">Sort: Avg age</option>
            </select>
            <button onClick={toggleDir} className="drawer-sort-btn">
              {sortDir === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>

        {/* Issue type filter chips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em', marginRight: 2 }}>Show:</span>
          {allTypes.map(type => {
            const color  = getTC(type);
            const active = selectedTypes.has(type);
            return (
              <button key={type} onClick={() => toggleType(type)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 100, border: `1px solid ${active ? color : 'var(--border)'}`, background: active ? color + '18' : 'var(--surface-2)', cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400, color: active ? color : 'var(--text-2)', transition: 'all .15s', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? color : 'var(--border)', flexShrink: 0, transition: 'background .15s' }} />
                {type}
              </button>
            );
          })}
          <button onClick={toggleAll}
            style={{ padding: '3px 10px', borderRadius: 100, border: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer', fontSize: 11, color: 'var(--text-2)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {allSelected ? 'None' : 'All'}
          </button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Assignee</th>
                <th>Tickets</th>
                <th>Load</th>
                <th>Issue Types</th>
                <th>Time Logged</th>
                <th>Avg Age</th>
                <th>Projects</th>
              </tr>
            </thead>
            <tbody>
              {assigned.map(person => {
                const fc = getFilteredCount(person);
                const critCount = (person.priorities['Critical'] || 0) + (person.priorities['Highest'] || 0) + (person.priorities['High'] || 0);
                const visibleTypes = Object.entries(person.issue_types || {})
                  .filter(([t]) => selectedTypes.size === 0 || selectedTypes.has(t));
                return (
                  <tr key={person.assignee} onClick={() => setSelected(person.assignee)}
                    tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setSelected(person.assignee); } }}
                    aria-label={`${person.assignee}: ${fc} tickets. Open details`}
                    style={{ cursor: 'pointer', opacity: fc === 0 ? 0.4 : 1 }}
                    onMouseEnter={e => { Array.from(e.currentTarget.cells).forEach(c => c.style.background = 'var(--accent-light)'); }}
                    onMouseLeave={e => { Array.from(e.currentTarget.cells).forEach(c => c.style.background = ''); }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={person.assignee} />
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{person.assignee}</div>
                          {critCount > 0 && <div style={{ fontSize: 11, color: 'var(--critical)' }}>⚠ {critCount} critical/high</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 15 }}>{fc}</td>
                    <td style={{ minWidth: 100 }}>
                      <div className="bar-bg">
                        <div className="bar-fill" style={{ width: `${(fc / maxFilteredCount) * 100}%` }} />
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {visibleTypes.length > 0
                          ? visibleTypes.map(([t, c]) => (
                              <span key={t} style={{ fontSize: 11, padding: '1px 7px', borderRadius: 100, background: getTC(t) + '22', color: getTC(t), fontWeight: 600 }}>{c} {t}</span>
                            ))
                          : <span style={{ fontSize: 11, color: 'var(--text-2)' }}>—</span>
                        }
                      </div>
                    </td>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>{person.time_spent || '—'}</td>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: person.avg_age_days > 90 ? 'var(--critical)' : person.avg_age_days > 30 ? 'var(--warn)' : 'var(--text)' }}>{person.avg_age_days}d</td>
                    <td>{person.projects.map(p => <span key={p} className="pill">{p}</span>)}</td>
                  </tr>
                );
              })}

              {unassigned && (() => {
                const fc = getFilteredCount(unassigned);
                const visibleTypes = Object.entries(unassigned.issue_types || {})
                  .filter(([t]) => selectedTypes.size === 0 || selectedTypes.has(t));
                return (
                  <tr onClick={() => setSelected('Unassigned')}
                    tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setSelected('Unassigned'); } }}
                    aria-label={`Unassigned: ${fc} tickets. Open details`}
                    style={{ cursor: 'pointer', background: 'var(--warn-light)', opacity: fc === 0 ? 0.4 : 1 }}>
                    <td><span style={{ fontWeight: 600, color: 'var(--warn)' }}>Unassigned</span></td>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 15, color: 'var(--warn)' }}>{fc}</td>
                    <td><div className="bar-bg"><div className="bar-fill" style={{ width: `${(fc / maxFilteredCount) * 100}%`, background: 'var(--warn)' }} /></div></td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {visibleTypes.map(([t, c]) => (
                          <span key={t} style={{ fontSize: 11, padding: '1px 7px', borderRadius: 100, background: getTC(t) + '22', color: getTC(t), fontWeight: 600, marginRight: 3 }}>{c} {t}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>{unassigned.time_spent || '—'}</td>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>{unassigned.avg_age_days}d</td>
                    <td>{unassigned.projects.map(p => <span key={p} className="pill">{p}</span>)}</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <AssigneeDrawer api={api} uploadId={uploadId} assignee={selected} onClose={() => setSelected(null)} jiraUrl={globalJiraUrl} />
      )}
    </div>
  );
}
